/**
 * Сайдбар модулей ERP (UI-1). Дерево — utils/erp-modules.js; переходы по data-route
 * делает router.js, здесь подсветка, состояние (localStorage ccpm_sidebar_state),
 * счётчики и мобильный drawer. После сворачивания шлём window resize по transitionend,
 * gantt.setSizes() напрямую не зовём (синглтон живёт только в views/gantt.js).
 */
import { canDo } from '../auth.js';
import { getBasePath, router } from '../router.js';
import { MODULES, findItem, moduleTarget, visibleItems } from '../utils/erp-modules.js';
import { fetchSidebarCounts } from '../utils/sidebar-counts.js';
import { iconChevronRight, iconModule, iconPanelLeft } from '../utils/icons.js';

const STORAGE_KEY = 'ccpm_sidebar_state';
const LAST_KEY = 'ccpm_sidebar_last';     // { moduleKey: последний посещённый путь }
const STORAGE_VERSION = 1;
const MOBILE_MAX_WIDTH = 900;        // синхронно с @media в styles/sidebar.css
const COUNTS_INTERVAL_MS = 60_000;

let _aside = null;
let _backdrop = null;
let _state = defaultState();
let _bound = false;
let _countsTimer = null;
let _resizeFallback = null;

function defaultState() {
  return { collapsed: false, open: { ccpm: true } };
}

function loadState() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (!parsed || parsed.version !== STORAGE_VERSION) return defaultState();
    return { collapsed: !!parsed.collapsed, open: { ...(parsed.open || {}) } };
  } catch {
    return defaultState();
  }
}

function saveState() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: STORAGE_VERSION, ..._state }));
  } catch {
    /* storage недоступен — состояние живёт до перезагрузки */
  }
}

const isMobile = () => window.innerWidth < MOBILE_MAX_WIDTH;

function loadLast() {
  try { return JSON.parse(localStorage.getItem(LAST_KEY) || '{}') || {}; } catch { return {}; }
}

function saveLast(moduleKey, path) {
  try {
    localStorage.setItem(LAST_KEY, JSON.stringify({ ...loadLast(), [moduleKey]: path }));
  } catch {
    /* storage недоступен */
  }
}

/** Путь для клика по модулю (последний пункт или первый, у CCPM — Gantt). */
const targetOf = (mod) => moduleTarget(mod, canDo, loadLast()[mod.key]);

function renderItem(it) {
  let right = '';
  if (it.count) {
    right = `<span class="sidebar-count sidebar-count--${it.tone || 'attention'}" data-count="${it.count}" hidden>0</span>`;
  } else if (!it.view) {
    const wave = it.wave || 'В2';
    right = `<span class="sidebar-wave" title="Волна ${wave}">${wave}</span>`;
  }
  return `<li><a href="${it.path}" data-route="${it.path}" class="sidebar-item" title="${it.label}">
    <span class="sidebar-item-label">${it.label}</span>${right}</a></li>`;
}

function renderModule(mod) {
  const open  = !!_state.open[mod.key];
  const badge = mod.badge
    ? `<span class="sidebar-badge sidebar-badge--${mod.badge.tone}" data-badge="${mod.badge.key}" hidden>0</span>`
    : '';
  // Название ведёт в модуль (последний или первый пункт), шеврон только сворачивает список.
  return `
    <div class="sidebar-module${open ? ' is-open' : ''}" data-module="${mod.key}">
      <div class="sidebar-module-btn">
        <a href="${targetOf(mod) ?? '#'}" class="sidebar-module-go" data-module-go="${mod.key}" title="${mod.label}">
          <span class="sidebar-icon">${iconModule(mod.key, 18)}</span>
          <span class="sidebar-label">${mod.label}</span>${badge}
        </a>
        <button type="button" class="sidebar-chevron" data-module-toggle="${mod.key}"
                aria-expanded="${open}" aria-label="Свернуть или развернуть «${mod.label}»">${iconChevronRight(14)}</button>
      </div>
      <ul class="sidebar-items">${visibleItems(mod, canDo).map(renderItem).join('')}</ul>
    </div>`;
}

function render() {
  // Кнопка сворачивания — «язычок» на правой границе сайдбара, вне скроллящегося nav
  // и опущен к ~1/4 высоты (CSS: .sidebar-collapse, position:absolute). Подпись скрыта,
  // остаётся только иконка с тултипом; applyCollapsed() обновляет title/aria/иконку как прежде.
  _aside.innerHTML = `
    <button type="button" class="sidebar-collapse" data-sidebar-collapse title="Свернуть">
      <span class="sidebar-icon sidebar-collapse-icon"></span>
      <span class="sidebar-label"></span>
    </button>
    <nav class="sidebar-nav" aria-label="Модули">${MODULES.map(renderModule).join('')}</nav>`;
  applyCollapsed();
}

function applyCollapsed() {
  _aside.classList.toggle('is-collapsed', _state.collapsed);
  // Шапка — сосед сайдбара, CSS-селектором его состояние не достать: дублируем
  // флаг на #app, чтобы в свёрнутом сайдбаре надпись бренда пряталась (БРЕНД-1).
  document.getElementById('app')?.classList.toggle('is-sidebar-collapsed', _state.collapsed);
  const btn = _aside.querySelector('[data-sidebar-collapse]');
  if (!btn) return;
  const label = _state.collapsed ? 'Развернуть' : 'Свернуть';
  btn.title = label;
  btn.setAttribute('aria-expanded', String(!_state.collapsed));
  btn.querySelector('.sidebar-label').textContent = label;
  btn.querySelector('.sidebar-collapse-icon').innerHTML = iconPanelLeft(!_state.collapsed, 18);
}

function fireResize() {
  clearTimeout(_resizeFallback);
  _resizeFallback = null;
  window.dispatchEvent(new Event('resize'));
}

function setCollapsed(collapsed) {
  if (_state.collapsed === collapsed) return;
  _state.collapsed = collapsed;
  saveState();
  applyCollapsed();
  // Основной сигнал — transitionend по width; таймер на случай, если transition не запустился.
  clearTimeout(_resizeFallback);
  _resizeFallback = setTimeout(fireResize, 350);
}

function setModuleOpen(key, open) {
  const el = _aside.querySelector(`.sidebar-module[data-module="${key}"]`);
  if (!el) return;
  el.classList.toggle('is-open', open);
  el.querySelector('[data-module-toggle]')?.setAttribute('aria-expanded', String(open));
  if (open) _state.open[key] = true; else delete _state.open[key];
  saveState();
}

/** Подсветка активного подпункта и модуля по текущему маршруту. */
function syncActive(path) {
  const basePath = getBasePath(path);
  const found    = findItem(basePath);
  _aside.querySelectorAll('.sidebar-item').forEach(a => {
    a.classList.toggle('active', a.dataset.route === basePath);
  });
  _aside.querySelectorAll('.sidebar-module').forEach(m => {
    m.classList.toggle('is-active', m.dataset.module === found?.module.key);
  });
  if (found && !_state.open[found.module.key]) setModuleOpen(found.module.key, true);
  if (found) {
    saveLast(found.module.key, basePath);
    const go = _aside.querySelector(`[data-module-go="${found.module.key}"]`);
    if (go) go.setAttribute('href', basePath);
  }
}

function openDrawer(open) {
  _aside.classList.toggle('is-open', open);
  if (_backdrop) _backdrop.hidden = !open;
  document.querySelector('[data-sidebar-toggle]')?.setAttribute('aria-expanded', String(open));
}

function onClick(e) {
  const t = e.target;
  if (!(t instanceof Element)) return;

  const go = t.closest('[data-module-go]');
  if (go && _aside.contains(go)) {
    e.preventDefault();
    const mod = MODULES.find(m => m.key === go.dataset.moduleGo);
    const path = mod && targetOf(mod);
    if (mod) setModuleOpen(mod.key, true);
    if (isMobile()) openDrawer(false);
    if (path && path !== location.pathname) router.push(path);
    return;
  }
  const toggle = t.closest('[data-module-toggle]');
  if (toggle && _aside.contains(toggle)) {
    const key = toggle.dataset.moduleToggle;
    setModuleOpen(key, !toggle.closest('.sidebar-module').classList.contains('is-open'));
    return;
  }
  if (t.closest('[data-sidebar-collapse]')) { setCollapsed(!_state.collapsed); return; }
  if (t.closest('[data-sidebar-toggle]'))   { openDrawer(!_aside.classList.contains('is-open')); return; }
  if (t === _backdrop)                      { openDrawer(false); return; }
  if (isMobile() && _aside.contains(t) && t.closest('[data-route]')) openDrawer(false);
}

function bind() {
  if (_bound) return;
  _bound = true;
  document.addEventListener('click', onClick);
  document.addEventListener('ccpm:navigate', e => syncActive(e.detail?.path ?? location.pathname));
  _aside.addEventListener('transitionend', e => {
    if (e.target === _aside && e.propertyName === 'width') fireResize();
  });
  window.addEventListener('resize', () => {
    if (!isMobile() && _aside.classList.contains('is-open')) openDrawer(false);
  });
}

function applyCounts(counts) {
  const fill = (selector, key) => _aside.querySelectorAll(selector).forEach(el => {
    const v = Number(counts?.[el.dataset[key]] ?? 0);
    el.textContent = String(v);
    el.hidden = !(v > 0);
  });
  fill('[data-badge]', 'badge');
  fill('[data-count]', 'count');
}

async function refreshCounts() {
  if (document.hidden) return;
  try {
    applyCounts(await fetchSidebarCounts());
  } catch {
    /* бейджи некритичны */
  }
}

function startCounts() {
  refreshCounts();
  if (_countsTimer) clearInterval(_countsTimer);
  _countsTimer = setInterval(refreshCounts, COUNTS_INTERVAL_MS);
}

export function initSidebar() {
  _aside = document.getElementById('app-sidebar');
  if (!_aside) return;
  _backdrop = document.getElementById('sidebar-backdrop');
  _state = loadState();
  render();
  bind();
  syncActive(location.pathname);
  startCounts();
}
